import { useEffect, useRef, useState } from "react";

/**
 * Tiny fetch hook with a process-wide cache + in-flight dedupe.
 *
 * One Promise per key — if three components mount with the same key, they
 * share one network request and one cached result. Cache survives navigation
 * (it's module-level, not component-level). Pass a new key (or call refetch)
 * to invalidate.
 *
 * `invalidate(prefix)` wakes every active consumer whose key matches, so a
 * mutation elsewhere refreshes the dependent reads immediately.
 *
 * Revalidation is **stale-while-revalidate**: a consumer that already has data
 * for its key keeps showing it while the refetch runs, then swaps. Only a real
 * key change (navigation) goes through "loading". This matters beyond polish:
 * App gates the whole tree on the session fetch, so if a live `git` event
 * flipped it to loading, every mounted component — including an editor holding
 * unsaved text — would unmount.
 */
const cache = new Map<string, unknown>();
const inflight = new Map<string, Promise<unknown>>();
const subscribers = new Map<string, Set<() => void>>();

function subscribe(key: string, cb: () => void): () => void {
  let set = subscribers.get(key);
  if (!set) {
    set = new Set();
    subscribers.set(key, set);
  }
  set.add(cb);
  return () => {
    set?.delete(cb);
    if (set && set.size === 0) subscribers.delete(key);
  };
}

export type FetchState<T> =
  | { status: "loading"; data: null; error: null }
  | { status: "ready"; data: T; error: null }
  | { status: "error"; data: null; error: Error };

export interface LoaderOpts {
  /** True when the loader is being invoked due to refetch(), not the first fetch. */
  refetched: boolean;
}

export function useFetch<T>(
  key: string | null,
  loader: (opts: LoaderOpts) => Promise<T>,
): FetchState<T> & { refetch: () => void } {
  const initial: FetchState<T> =
    key !== null && cache.has(key)
      ? { status: "ready", data: cache.get(key) as T, error: null }
      : { status: "loading", data: null, error: null };
  const [state, setState] = useState<FetchState<T>>(initial);
  const [version, setVersion] = useState(0);
  const lastKey = useRef(key);

  // Subscribe so external invalidate() calls wake us up.
  useEffect(() => {
    if (key === null) return;
    return subscribe(key, () => setVersion((n) => n + 1));
  }, [key]);

  useEffect(() => {
    if (key === null) return;
    let cancelled = false;
    const keyChanged = lastKey.current !== key;
    lastKey.current = key;

    if (cache.has(key)) {
      setState({ status: "ready", data: cache.get(key) as T, error: null });
      return;
    }
    // Same key, cache dropped (invalidate/refetch): keep the stale data on
    // screen and revalidate in the background. A key change shows loading.
    if (keyChanged) setState({ status: "loading", data: null, error: null });

    let p = inflight.get(key) as Promise<T> | undefined;
    if (!p) {
      p = loader({ refetched: version > 0 })
        .then((v) => {
          cache.set(key, v);
          return v;
        })
        .finally(() => {
          inflight.delete(key);
        });
      inflight.set(key, p);
    }
    p.then((v) => {
      if (!cancelled) setState({ status: "ready", data: v, error: null });
    }).catch((err: unknown) => {
      if (cancelled) return;
      // A failed background revalidation keeps the stale data — flipping a
      // rendered page (or the session gate) to error over a blip is worse.
      setState((prev) =>
        prev.status === "ready" && !keyChanged
          ? prev
          : {
              status: "error",
              data: null,
              error: err instanceof Error ? err : new Error(String(err)),
            },
      );
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, version]);

  const refetch = (): void => {
    if (key === null) return;
    cache.delete(key);
    inflight.delete(key);
    setVersion((n) => n + 1);
  };

  return { ...state, refetch };
}

export function invalidate(prefix: string): void {
  for (const k of [...cache.keys()]) if (k.startsWith(prefix)) cache.delete(k);
  for (const k of [...inflight.keys()]) if (k.startsWith(prefix)) inflight.delete(k);
  for (const k of [...subscribers.keys()])
    if (k.startsWith(prefix)) subscribers.get(k)?.forEach((cb) => cb());
}
